import type { CapacityTier, HeatmapTier, ProgramLabel, SessionType, Weekday } from '../types/weekOverview';

// Column order for the week overview displays (calendar + heatmap): all seven
// days, Monday–Sunday. The clinic has no weekend scheduling, so Sat/Sun render
// as empty calendar columns and closed heatmap cells. Also the index basis for
// weekday→date offsets (see getDateForWeekday), so it must stay Mon-first.
export const WEEKDAYS_ORDER: Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Days the clinic actually schedules — person-day loads always cover these,
// so weekend columns stay closed unless someone genuinely works a weekend.
// The display order lives in WEEKDAYS_ORDER above.
export const SCHEDULED_WEEKDAYS: Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI'];

export const WEEKDAY_TRANSLATION_KEYS: Record<Weekday, string> = {
  MON: 'weekday-short-mon',
  TUE: 'weekday-short-tue',
  WED: 'weekday-short-wed',
  THU: 'weekday-short-thu',
  FRI: 'weekday-short-fri',
  SAT: 'weekday-short-sat',
  SUN: 'weekday-short-sun'
};

export const WEEKDAY_FULL_TRANSLATION_KEYS: Record<Weekday, string> = {
  MON: 'weekday-full-mon',
  TUE: 'weekday-full-tue',
  WED: 'weekday-full-wed',
  THU: 'weekday-full-thu',
  FRI: 'weekday-full-fri',
  SAT: 'weekday-full-sat',
  SUN: 'weekday-full-sun'
};

// A person's daily load is measured against a single availability pool (their
// full working hours). Utilization never exceeds 100%. The thresholds define
// three tiers:
//   healthy   < 70%   — comfortable load
//   watch     70–84%  — getting tight, rebalance proactively
//   critical  ≥ 85%   — eating into protected break/lunch time (the disaster zone)
// 85% is the BREAK-PROTECTED CEILING: hitting it means no breaks left, which
// must never happen. It is drawn as a marker line on every load bar.
export const CAPACITY_WATCH_THRESHOLD = 70;
export const CAPACITY_CEILING = 85;

export const CAPACITY_TIER_COLORS: Record<CapacityTier, { background: string; text: string; border: string }> = {
  healthy: {
    background: 'var(--color-success-1)',
    text: 'var(--color-success-3)',
    border: 'var(--color-success-2)'
  },
  watch: {
    background: 'var(--color-warning-1)',
    text: 'var(--color-warning-4)',
    border: 'var(--color-warning-2)'
  },
  critical: {
    background: 'var(--color-error-3)',
    text: 'var(--color-text-invert)',
    border: 'var(--color-error-3)'
  },
  absent: {
    background: 'var(--color-surface-secondary)',
    text: 'var(--color-text-secondary)',
    border: 'var(--color-border-primary)'
  }
};

// Skill coverage uses a distinct sequential-BLUE ramp (darker = more loaded) so
// it is never confused with the staff-load traffic-light (green/amber/red).
// The colour reflects the least-busy qualified person's load tier; "no coverage"
// (nobody qualified on shift) is shown separately as a neutral dashed cell.
export const SKILL_COVERAGE_TIER_COLORS: Record<CapacityTier, { background: string; text: string; border: string }> = {
  healthy: {
    background: 'var(--color-graphics-graph-first-1)',
    text: 'var(--color-graphics-graph-first-3)',
    border: 'var(--color-graphics-graph-first)'
  },
  watch: {
    background: 'var(--color-graphics-graph-first)',
    text: 'var(--color-interaction-primary)',
    border: 'var(--color-graphics-graph-first-2)'
  },
  critical: {
    background: 'var(--color-graphics-graph-first-3)',
    text: 'var(--color-text-invert)',
    border: 'var(--color-graphics-graph-first-3)'
  },
  absent: {
    background: 'var(--color-surface-secondary)',
    text: 'var(--color-text-secondary)',
    border: 'var(--color-border-primary)'
  }
};

// --- v4 capacity heatmap tiers ---
// Utilization (booked ÷ available) is capped at 100%; thresholds split the
// 0–100 range into healthy / tight / full / critical. These are the original v4
// values (more aggressive than the per-person capacity tiers above): green only
// below 60%, red at 95%+.
export const HEATMAP_THRESHOLDS = {
  healthy: 60,
  tight: 80,
  full: 95
} as const;

// Shown instead of a percentage when the location is closed that day.
export const HEATMAP_CLOSED_CELL_TEXT = '—';

// The heatmap renders a single tooltip element, so interactive cells can point
// at it with a fixed aria-describedby while they are focused.
export const HEATMAP_TOOLTIP_ID = 'week-overview-heatmap-tooltip';

// Matches the data attribute every arrow-reachable heatmap cell renders — the
// activatable ones and the inert ones alike. Arrow-key navigation resolves a
// target cell id (see heatmapNavigationService) and finds the button to focus
// with this selector, comparing ids via `dataset` so cell ids never need CSS
// escaping.
export const HEATMAP_CELL_SELECTOR = '[data-heatmap-cell-id]';

export const HEATMAP_TIER_COLORS: Record<HeatmapTier, { background: string; text: string; border: string }> = {
  healthy: {
    background: 'var(--color-success-1)',
    text: 'var(--color-success-3)',
    border: 'var(--color-success-2)'
  },
  tight: {
    background: 'var(--color-warning-1)',
    text: 'var(--color-warning-4)',
    border: 'var(--color-warning-2)'
  },
  full: {
    background: '#F4C28A',
    text: '#7A3F0F',
    border: '#E8A05E'
  },
  critical: {
    background: 'var(--color-error-3)',
    text: 'var(--color-text-invert)',
    border: 'var(--color-error-3)'
  },
  closed: {
    background: '#C9C5C0',
    text: '#5B5854',
    border: '#A8A39E'
  },
  empty: {
    background: 'var(--color-surface-secondary)',
    text: 'var(--color-text-secondary)',
    border: 'var(--color-border-primary)'
  }
};

export const CALENDAR_START_HOUR = 7;
export const CALENDAR_END_HOUR = 18;
export const CALENDAR_HOUR_HEIGHT = 96;
// Shorter rows when printing so the whole day fits on one page (leaving room
// for larger, more legible print fonts).
export const CALENDAR_PRINT_HOUR_HEIGHT = 72;
export const CALENDAR_VERTICAL_PADDING = 16;

// Colors for each session type's calendar block (background, border, text).
// Keyed by SessionType so the map stays exhaustive — a new session type fails
// to compile until it is coloured here. These are intentional literals: the
// design-token set has no session-type palette (only the generic graph ramp),
// so unlike the capacity maps above they cannot be expressed as
// var(--color-*) tokens.
export const SESSION_TYPE_COLORS: Record<SessionType, { background: string; border: string; text: string }> = {
  INDIVIDUAL: {
    background: '#E4ECFF',
    border: '#89A6FB',
    text: '#1F2C5C'
  },
  GROUP: {
    background: '#FCE3D6',
    border: '#D48C72',
    text: '#5B2A14'
  },
  COURSE: {
    background: '#FFE8D5',
    border: '#E89C5F',
    text: '#6B3712'
  },
  MEETING: {
    background: '#EAE8F2',
    border: '#A0A4BB',
    text: '#2F3257'
  },
  PROJECT: {
    background: '#E6E2F2',
    border: '#7871AA',
    text: '#3A356B'
  },
  ASSESSMENT: {
    background: '#E5E0F0',
    border: '#7871AA',
    text: '#3A356B'
  },
  LUNCH: {
    background: '#F1EBE4',
    border: '#D9D2C9',
    text: '#5B5854'
  }
};

// User-facing label for each session type. Keyed by SessionType so the map
// stays exhaustive — a new session type fails to compile until it gets a
// translation key here.
export const SESSION_TYPE_TRANSLATION_KEYS: Record<SessionType, string> = {
  INDIVIDUAL: 'week-overview-session-type-individual',
  GROUP: 'week-overview-session-type-group',
  COURSE: 'week-overview-session-type-course',
  MEETING: 'week-overview-session-type-meeting',
  PROJECT: 'week-overview-session-type-project',
  ASSESSMENT: 'week-overview-session-type-assessment',
  LUNCH: 'week-overview-session-type-lunch'
};

// Matches the data attribute InterventionCard renders on every calendar card.
// The detail popover's outside-click handler uses it so a click on another
// card swaps the popover (via the card's own click handler) instead of merely
// dismissing it.
export const INTERVENTION_CARD_SELECTOR = '[data-intervention-card="true"]';

// Display names for the treatment programs. These are the programs' proper
// (Danish) names, not translatable copy, so they live here rather than in
// translations.json. Keyed by ProgramLabel so the map stays exhaustive.
export const PROGRAM_LABEL_DISPLAY_NAMES: Record<ProgramLabel, string> = {
  SMERTELIVET: 'Smertelivet',
  ARBEJDSLIVET: 'Arbejdslivet'
};

// Accent colour for each KPI summary card's icon badge and feature highlight.
// Each value is a design token so the palette stays in sync with the theme.
export const KPI_ACCENT_COLORS = {
  occupancy: 'var(--color-interaction-main)',
  distribution: 'var(--color-graphics-graph-first)',
  staff: 'var(--color-graphics-graph-third)',
  treatments: 'var(--color-graphics-graph-second)',
  patients: 'var(--color-success-primary)'
} as const;

// Segment colours for the task-distribution stacked bar and its legend. Keyed
// by task category so the bar and legend always agree on the colour mapping.
// The intervention sub-category split (individual/group/internal as shades of
// one colour) is a pending design refactor — REH-145.
export const KPI_DISTRIBUTION_COLORS = {
  interventions: 'var(--color-graphics-graph-first)',
  vacation: 'var(--color-graphics-graph-third)',
  otherBookings: 'var(--color-graphics-graph-second)',
  whiteTime: 'var(--color-surface-tertiary)'
} as const;
